/****************************************************************************************
 * Copyright (c) 2023. Peter Lemke, SV-Studios                                          *
 * This File, its contents and by extension the corresponding project is property of    *
 * SV-Studios and may not be used without explicit permission to do so.                 *
 *                                                                                      *
 * management@sv-studios.net                                                            *
 ****************************************************************************************/

import { useFlag } from "@unleash/proxy-client-react";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { flagsAction } from "./flags-slice.js";
import config, { devModeEnabled } from "../../config";
import { client } from "../../index";

const flagPrefix = () => (devModeEnabled() ? config.local.unleash.flagPrefix : config.unleash.flagPrefix);

/**
 * useUnleashFlag - A hook to handle the flags of the website
 * @param type {string}
 * @param force {boolean}
 * @return {boolean}
 */
export function useUnleashFlag(type, force = false) {
  const enabled = useFlag((flagPrefix() + "." || "") + type);
  const optIn = useSelector((state) => state.flags.enabled[flagPrefix() + "." + type]);

  return useMemo(() => enabled && (force || optIn), [enabled, optIn]);
}

export const loadFlagsFromLocalStorage = createAsyncThunk(
  "flags/loadFromLocalStorage",
  async (_, { dispatch }) => {
    const savedFlags = localStorage.getItem("flags");
    if (savedFlags) {
      const parsedFlags = JSON.parse(savedFlags);
      parsedFlags.forEach((flag) => {
        dispatch(flagsAction.toggleFlag(flag));
      });
    }
  }
);

export const toggleAndSaveFlag = createAsyncThunk(
  "flags/toggleAndSave",
  async (flag, { dispatch, getState }) => {
    // Zustand toggeln
    dispatch(flagsAction.toggleFlag(flag));

    // Aktuellen Zustand holen und in Local Storage speichern
    const currentFlags = getState().flags?.enabled;
    // curent flags is an object, not an array {flag: true, flag2: false} filter if value is true

    const filteredFlags = Object.keys(currentFlags).filter((flag) => currentFlags[flag]);

    localStorage.setItem("flags", JSON.stringify(filteredFlags));
  }
);

export const loadAndInitializeToggles = createAsyncThunk(
  "toggles/loadAndInitialize",
  async (_, { dispatch }) => {
    const allToggles = client.getAllToggles().filter((toggle) => toggle.name.startsWith(flagPrefix()));

    dispatch(flagsAction.initializeFlags(allToggles.map((toggle) => toggle.name)));

    // Redux-Action zum Initialisieren des States
    dispatch(loadFlagsFromLocalStorage());
  }
);
